import { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { playerService } from "../../services/api";
import DetailedPositionsControl from "../common/DetailedPositionsControl";
import NationalityFlags from "../common/NationalityFlags";
import ListAsAvailableButton from "../last-minute/ListAsAvailableButton";

const AgentPlayerCard = ({
  player,
  showStatus = true,
  onDetailedPositionsChange,
  onContractExpiryChange,
  canEditPositions = false,
  whereFrom = "mysquad",
  // Gates the owner-only "List as available" action. Public profiles
  // (Agency/AgentPublicProfile) pass false so the Last Minute entry point never
  // surfaces on a public surface (M2 review F1).
  showQuickActions = true,
  // The player's active Last Minute listing, if any (design 6a already-listed).
  lmListing = null,
}) => {
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();

  const handlePlayerClick = () => {
    // Same id chain the position/contract handlers below resolve. A player
    // without one used to land on /search/player/null, which the profile
    // answers with a bare 500, so the click stays inert and says why
    // (MyPlayers.js guards the same route).
    const wyscoutPlayerId =
      player.wyscout_id ||
      player.wyscoutId ||
      player.wyscout_player?.id ||
      player.ws_player_id;

    if (!wyscoutPlayerId) {
      const identifier = player?.name || "player";
      toast.error(`Couldn't open the profile for ${identifier}.`);
      return;
    }

    navigate(`/search/player/${wyscoutPlayerId}`);
  };

  const handleDetailedPositionsSave = async ({ formattedPositions }) => {
    const playerId =
      player.account_id ||
      player.player_id ||
      player.id ||
      player.playerAccountId;
    const wyscoutPlayerId =
      player.wyscout_id ||
      player.wyscoutId ||
      player.wyscout_player?.id ||
      player.ws_player_id;

    if (!playerId && !wyscoutPlayerId) {
      const identifier = player?.name || "player";
      toast.error(`Couldn't identify ${identifier} for updating positions.`);
      throw new Error(
        "Missing player identifier for detailed positions update"
      );
    }

    const nextDetailedPositions = { ...formattedPositions };
    Object.keys(nextDetailedPositions).forEach((key) => {
      if (!nextDetailedPositions[key]) {
        delete nextDetailedPositions[key];
      }
    });

    const payloadItem = {
      tp_detailed_position: nextDetailedPositions,
    };

    if (playerId) {
      payloadItem.player_id = playerId;
    }
    if (wyscoutPlayerId) {
      payloadItem.wyscout_player_id = wyscoutPlayerId;
    }

    const response = await playerService.updatePlayersDetailedPosition([
      payloadItem,
    ]);

    if (!response.success) {
      toast.error(response.error || "Failed to update detailed positions");
      throw new Error(response.error || "Failed to update detailed positions");
    }

    if (response.errors && response.errors.length) {
      const firstError =
        typeof response.errors[0] === "string"
          ? response.errors[0]
          : response.errors[0]?.message;
      toast.error(firstError || "Failed to update detailed positions");
      throw new Error(firstError || "Failed to update detailed positions");
    }

    toast.success("Detailed positions updated");
    onDetailedPositionsChange?.(player, nextDetailedPositions);
  };

  const handleContractExpirySave = async ({ contractExpiryDate }) => {
    const playerAccountId = player?.account_id;
    const wyscoutPlayerId = player?.wyscout_id;

    if (!playerAccountId && !wyscoutPlayerId) {
      const identifier = player?.name || "player";
      toast.error(`Couldn't identify ${identifier} for updating contract.`);
      throw new Error("Missing player identifier for contract update");
    }

    // Format date to YYYY-MM-DD
    const formattedDate = new Date(contractExpiryDate)
      .toISOString()
      .split("T")[0];

    const payloadItem = {
      contract_expiration: formattedDate,
    };

    if (playerAccountId) {
      payloadItem.player_account_id = playerAccountId;
    }
    if (wyscoutPlayerId) {
      payloadItem.wyscout_player_id = wyscoutPlayerId;
    }

    const response = await playerService.updateAgentPlayerContract([
      payloadItem,
    ]);

    if (!response.success) {
      toast.error(response.error || "Failed to update contract expiry");
      throw new Error(response.error || "Failed to update contract expiry");
    }

    if (response.errors && response.errors.length) {
      const firstError =
        typeof response.errors[0] === "string"
          ? response.errors[0]
          : response.errors[0]?.message;
      toast.error(firstError || "Failed to update contract expiry");
      throw new Error(firstError || "Failed to update contract expiry");
    }

    toast.success("Contract expiry updated successfully");
    
    // Update parent component's state
    if (onContractExpiryChange) {
      onContractExpiryChange(player, formattedDate);
    }
  };

  const handlePhotoUpload = async ({ file }) => {
    // Same id chain the position handler resolves; the upload endpoint keys
    // exclusively on the wyscout player id.
    const wyscoutPlayerId =
      player.wyscout_id ||
      player.wyscoutId ||
      player.wyscout_player?.id ||
      player.ws_player_id;

    if (!wyscoutPlayerId) {
      const identifier = player?.name || "player";
      toast.error(`Couldn't identify ${identifier} for uploading a photo.`);
      throw new Error("Missing player identifier for photo upload");
    }

    const response = await playerService.uploadAgentPlayerPhoto(
      wyscoutPlayerId,
      file
    );

    if (!response.success) {
      toast.error(
        response.error || "Failed to upload the photo. Please try again."
      );
      throw new Error(
        response.error || "Failed to upload the photo. Please try again."
      );
    }

    toast.success(
      "Photo submitted for review. It can take up to 24 hours until the new profile photo is visible."
    );
  };

  const detailedPositions =
    player.tp_detailed_position || player.tpDetailedPosition || {};

  function getAgeFromDOB(dob) {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  }

  const calculateContractExpiring = (mandateEndDate) => {
    if (!mandateEndDate) return "N/A";
    const today = new Date();
    const endDate = new Date(mandateEndDate);
    const timeDiff = endDate - today;

    if (timeDiff <= 0) return "Expired";

    const days = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    if (days < 30) {
      return `${days} days`;
    } else if (days < 365) {
      const months = Math.floor(days / 30);
      return `${months} month${months > 1 ? "s" : ""}`;
    } else {
      const years = Math.floor(days / 365);
      const remainingMonths = Math.floor((days % 365) / 30);
      if (remainingMonths > 0) {
        return `${years} year${years > 1 ? "s" : ""} ${remainingMonths} month${
          remainingMonths > 1 ? "s" : ""
        }`;
      }
      return `${years} year${years > 1 ? "s" : ""}`;
    }
  };

  const calculateAge = () => {
    if (player?.age) {
      return player.age;
    }
    if (player?.date_of_birth) {
      return getAgeFromDOB(player.date_of_birth);
    }
    return "N/A";
  };

  const calculateMandate = () => {
    if (player?.contract_expires_on) {
      return `${calculateContractExpiring(player.contract_expires_on)}`;
    }
    return "N/A";
  };

  return (
    <div
      className="bg-white rounded-lg p-6 hover:shadow transition-shadow border border-[#D2D4D880]"
      onClick={handlePlayerClick}
    >
      <div className="flex flex-col items-start gap-4 mb-4">
        <div className="flex justify-between w-full">
          {player.player_image && !imageError ? (
            <img
              src={player.player_image}
              alt={player.name}
              className="w-16 h-16 rounded-lg object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center text-xl font-bold text-gray-600">
              {player.name?.charAt(0) || "P"}
            </div>
          )}

          <div className="flex items-start gap-2">
            {showStatus && (
              <div className="flex flex-col items-end gap-1">
                {player.forSale && (
                  <span className="px-2 py-1 text-xs font-medium text-white bg-[#049669] rounded h-fit">
                    FOR SALE
                  </span>
                )}
                {player.forLoan && (
                  <span className="px-2 py-1 text-xs font-medium text-white bg-[#FF7500] rounded h-fit">
                    FOR LOAN
                  </span>
                )}
              </div>
            )}

            {canEditPositions && (
              <DetailedPositionsControl
                player={player}
                onSave={handleDetailedPositionsSave}
                onContractExpirySave={handleContractExpirySave}
                onPhotoUpload={handlePhotoUpload}
              />
            )}
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <h3 className="text-xl font-bold text-[#242426]">
              {player.name || "N/A"}
            </h3>
          </div>
        </div>
      </div>

      <div className="flex bg-[#F7F7F7] p-2 rounded-lg mb-4 items-center gap-2 mt-1 justify-between w-full">
        <span className="text-sm text-[#1A212F] font-medium">
          {player.position || "-"}
        </span>
        {/* All citizenships, national team first. */}
        <NationalityFlags
          nationalities={player}
          className="w-4 h-3"
          wrapperClassName="flex flex-wrap items-center gap-1.5"
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-secondary">Primary Position</p>
          <p className="text-sm font-medium text-gray-900">
            {detailedPositions?.primary_position || "-"}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[13px] text-secondary">Secondary Position</p>
          <p className="text-sm font-medium text-gray-900">
            {detailedPositions?.secondary_position || "-"}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[13px] text-secondary">Tertiary Position</p>
          <p className="text-sm font-medium text-gray-900">
            {player.tertiary_position ||
              detailedPositions.teritory_position ||
              "-"}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-[13px] text-secondary">
            Games played in last season
          </p>
          <p className="text-sm font-medium text-gray-900">
            {player.games_played_last_season
              ? `${player.games_played_last_season} games`
              : "N/A"}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-secondary">
            Club contract expiring in
          </p>
          <p className="text-sm font-medium text-gray-900">
            {calculateMandate()}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[13px] text-secondary">Age</p>
          <p className="text-sm font-medium text-gray-900">{calculateAge()}</p>
        </div>
      </div>

      {/* Entry point (design 6a): List this player as available on Last Minute.
          Owner-only — hidden on public profiles (showQuickActions=false). */}
      {showQuickActions && (
        <div className="mt-4 pt-4 border-t border-[#EEF1F6]" onClick={(e) => e.stopPropagation()}>
          <ListAsAvailableButton
            playerId={player.id || player.player_id || player.account_id}
            listing={lmListing}
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};

export default AgentPlayerCard;
